import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { RequestSample } from '../app/shared/models/network-health.model';
import { NetworkHealthService } from '../services/network-health.service';

/**
 * NetworkLatencyInterceptor
 *
 * Observes every monitored HTTP request and reports samples to
 * NetworkHealthService.
 *
 * RULES (read before modifying):
 * 1. This interceptor is PURELY OBSERVATIONAL.
 *    - It never modifies request bodies, headers, or response data.
 *    - It always rethrows errors after recording them.
 *    - Existing AuthInterceptor behaviour is completely unaffected.
 *
 * 2. Registered AFTER AuthInterceptor so auth headers are already in place.
 *
 * 3. Excluded URL patterns are never monitored:
 *    - Static assets (/assets/, .svg, .pdf, .woff, .ttf)
 *    - Auth token refresh (/auth/refresh, /auth/login)
 *    - Health-check endpoints (/health, /ping)
 *    - WebSocket upgrade requests (wss://, ws://)
 *    - Fake backend (/__fake/)
 *
 * 4. The frontend timeout operator has been REMOVED intentionally.
 *    The service no longer uses API response latency to determine network state.
 *    Network state is derived from browser events and navigator.connection only.
 *
 * 5. Only genuine network errors (HTTP status 0) are reported as meaningful
 *    signals. Slow responses and timeouts are reported to the service but the
 *    service ignores the durationMs and isTimeout fields.
 */
@Injectable()
export class NetworkLatencyInterceptor implements HttpInterceptor {

  /** URL patterns that should NOT be measured. */
  private static readonly EXCLUDED_PATTERNS: string[] = [
    '/assets/',
    '/health',
    '/ping',
    '/auth/refresh',
    '/auth/login',
    '/__fake/',
    '.svg',
    '.pdf',
    '.woff',
    '.ttf',
    '.eot',
    '.ico',
    'wss://',
    'ws://',
  ];

  private networkHealthService: NetworkHealthService | null = null;

  constructor(private readonly injector: Injector) {}

  /**
   * Lazy-inject NetworkHealthService to avoid circular DI at construction time.
   */
  private getNetworkHealthService(): NetworkHealthService {
    if (!this.networkHealthService) {
      this.networkHealthService = this.injector.get(NetworkHealthService);
    }
    return this.networkHealthService;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

    if (!this.shouldMonitor(request.url)) {
      return next.handle(request);
    }

    const startedAt = Date.now();

    return next.handle(request).pipe(
      // NOTE: No timeout() operator here — we do not want artificial timeouts
      // to influence the network state machine. The service only reacts to
      // genuine network failures and browser connection-type changes.

      tap((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse) {
          // Report a successful sample. The service ignores durationMs —
          // it is recorded here only for snapshot/diagnostic purposes.
          this.report({
            url: request.urlWithParams,
            durationMs: Date.now() - startedAt,
            isNetworkError: false,
            isTimeout: false,
            startedAt,
          });
        }
      }),

      catchError((error: unknown) => {
        const durationMs = Date.now() - startedAt;

        if (error instanceof HttpErrorResponse) {
          // status === 0 means a genuine network failure (no internet, DNS error, etc.)
          // This is the ONLY error type the service acts on.
          const isNetworkError = error.status === 0;
          this.report({
            url: request.urlWithParams,
            durationMs,
            isNetworkError,
            isTimeout: false,
            startedAt,
          });
          // Always rethrow unchanged so existing error handlers work
          return throwError(() => error);
        }

        // Unknown error — rethrow unchanged
        return throwError(() => error);
      })
    );
  }

  private shouldMonitor(url: string): boolean {
    if (!url) return false;
    return !NetworkLatencyInterceptor.EXCLUDED_PATTERNS.some((pattern) =>
      url.includes(pattern)
    );
  }

  private report(sample: RequestSample): void {
    try {
      this.getNetworkHealthService().reportRequest(sample);
    } catch {
      // Never let monitoring failures affect the application
    }
  }
}

export const networkLatencyInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: NetworkLatencyInterceptor,
  multi: true,
};
